import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import moment from "moment";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import { IRegister } from "../../Admin";
import BookingsTable from "../tables/BookingsTable";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number | null;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  allRegisters: IRegister[];
  register_ids: Array<number>;
  sales_channels: Array<string>;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function Bookings() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const GetReportRun = useReportRun();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
    allRegisters: [],
    register_ids: [],
    sales_channels: [],
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
    void getRegisters();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];

    const childReportsRes = await GetReport({ parent_id: report.id }, true);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      sage_50_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-sage-50-export")?.id ?? null,
      quickbooks_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-quickbooks-export")?.id ?? null,
      quickbooks_2008_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-quickbooks-2008-export")?.id ?? null,
      quickbooks_online_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-quickbooks-online-export")?.id ?? null,
    }));
  }

  async function getReport(email: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        register_ids: state.register_ids,
        sales_channels: state.sales_channels,
        email,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        register_ids: state.register_ids,
        sales_channels: state.sales_channels,
        email,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.daily_sales.001")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        register_ids: state.register_ids,
        sales_channels: state.sales_channels,
        email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.daily_sales.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  async function getRegisters() {
    const registerRes = await GetRegisters(null, true);
    if (registerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.004")));
      return;
    }
    setState(prevState => ({ ...prevState, allRegisters: registerRes.data }));
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.daily_sales.003"),
    action: () => {
      if (moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }

  return (
    <Page
      title={t("Bookings")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.daily_sales.005"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.daily_sales.006"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.daily_sales.007"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.reports.pages.daily_sales.008"), url: "/admin/report" }]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <BookingsTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}

